import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import '@react-pdf-viewer/core/lib/styles/index.css';
import pdfjsPackage from 'pdfjs-dist/package.json';

const PDF_WORKER_URL = `https://unpkg.com/pdfjs-dist@${pdfjsPackage.version}/build/pdf.worker.min.js`;

/*
 * Finds a cited passage inside a document's extracted text.
 * DocxHighlightViewer imports these from this file too, so both viewers match the same way.
 *
 * Inputs (any may be null):
 *   chunk    the stored chunk the citation came from (where in the file)
 *   phrase   the verbatim words inside that chunk (what to highlight)
 *   caption  a table title, used to find the table when the chunk cannot be found
 *
 * The chunk is located first and the phrase is searched INSIDE it, so a short or
 * common phrase ("56.4", "overall survival") lights up the occurrence the citation
 * came from, not the first one in the file. Nothing is highlighted rather than a
 * guess: there is no single-word fallback.
 *
 * Matching is done on a "compact" string with all spaces and hyphens removed, so
 * the differences between stored chunk text and the file's text layer (missing or
 * extra spaces between PDF text items, words split by a line-break hyphen,
 * "long-term" vs "long term") do not stop a match.
 */

// =====================================================================
//  MARKDOWN STRIPPING
// =====================================================================

export const stripMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/`[^`]*`/g, ' ')
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^[=\-]{2,}\s*$/gm, '')
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, ' $1 ')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]*)\]\[[^\]]*\]/g, '$1')
    .replace(/(\*{3}|_{3})(.*?)\1/g, '$2')
    .replace(/(\*{2}|_{2})(.*?)\1/g, '$2')
    .replace(/([*_])(.*?)\1/g, '$2')
    .replace(/~~(.*?)~~/g, '$1')
    .replace(/^>\s?/gm, '')
    .replace(/^[\s]*[-*+]\s+/gm, '')
    .replace(/^[\s]*\d+\.\s+/gm, '')
    .replace(/^[-*_]{3,}\s*$/gm, '')
    .replace(/\|/g, ' ')
    .replace(/^[\s:|-]+$/gm, '')
    .replace(/^\[[^\]]+\]:\s*\S+.*$/gm, '')
    .replace(/\r\n|\r|\n/g, ' ')
    .replace(/\s{2,}/g, ' ')
    .trim();
};

// A stored chunk is indexer output, not text from the file: a table chunk starts with a generated
// summary ("This table contains ... with the following columns: ...") and carries pandas leftovers
// (|Unnamed: 2|, nan, |---|). None of that is in the file. Keep only what the file contains.
export const cleanChunk = (content) => {
  if (!content || typeof content !== 'string') return content;
  let text = content.replace(/\r/g, '');

  const firstRow = text.search(/^\s*\|/m);
  if (firstRow > 0) {
    const prefix = text.slice(0, firstRow);
    if (/^\s*(this (is|table)|the (table|following))/i.test(prefix) || /following columns/i.test(prefix)) {
      text = text.slice(firstRow);
    }
  }

  const lines = text.split('\n').map((line) => {
    if (!line.includes('|')) return line;
    return line
      .split('|')
      .map((c) => c.trim())
      .filter((c) => c && c.toLowerCase() !== 'nan' && !/^Unnamed:\s*\d+$/i.test(c) && !/^:?-{3,}:?$/.test(c))
      .join(' ');
  });

  const cleaned = lines.join('\n');
  return cleaned.replace(/\s+/g, ' ').trim().length >= 12 ? cleaned : content;
};

// =====================================================================
//  TEXT FOLDING  (one rule for the document and for the search text)
// =====================================================================

const DROP_RE = /[­​‌‍﻿]/;
const FOLD = {
  '‘': "'", '’': "'", '‚': "'", '‛': "'", '`': "'",
  '“': '"', '”': '"', '„': '"', '‟': '"',
  '‐': '-', '‑': '-', '‒': '-',
  '–': '-', '—': '-', '―': '-', '−': '-',
  '…': '...',
  'ﬁ': 'fi', 'ﬂ': 'fl', 'ﬀ': 'ff', 'ﬃ': 'ffi', 'ﬄ': 'ffl',
  ' ': ' ',
};

/** One source character -> zero or more folded characters (' ' for any separator). */
export const foldChar = (ch) => {
  if (DROP_RE.test(ch)) return '';
  if (FOLD[ch] !== undefined) return FOLD[ch];
  if (/[*_~#>|\\]/.test(ch)) return ' ';
  if (/\s/.test(ch)) return ' ';
  return ch.toLowerCase();
};

export const foldString = (s) => {
  let out = '';
  for (const ch of s || '') {
    for (const c of foldChar(ch)) {
      if (c === ' ') {
        if (out && out[out.length - 1] !== ' ') out += ' ';
      } else {
        out += c;
      }
    }
  }
  return out.trim();
};

const COMPACT_SKIP = /[\s-]/;
const compactOf = (s) => (s || '').replace(/[\s-]+/g, '');

/** The document text with spaces and hyphens removed, plus each kept char's index in `concat`. */
const buildCompactIndex = (concat) => {
  const chars = [];
  const pos = [];
  for (let i = 0; i < concat.length; i++) {
    const c = concat[i];
    if (COMPACT_SKIP.test(c)) continue;
    chars.push(c);
    pos.push(i);
  }
  return { compact: chars.join(''), pos };
};

const findAll = (hay, needle, from = 0, to = hay.length, cap = 20) => {
  const out = [];
  if (!needle) return out;
  let i = hay.indexOf(needle, Math.max(0, from));
  while (i !== -1 && i + needle.length <= to && out.length < cap) {
    out.push(i);
    i = hay.indexOf(needle, i + 1);
  }
  return out;
};

const searchWords = (raw) => foldString(stripMarkdown(raw)).split(' ').filter(Boolean);

// =====================================================================
//  LOCATE THE CHUNK
// =====================================================================

const PROBE_MIN_CHARS = 30;  // shorter probes match by chance in a long document
const PROBE_MIN_WORDS = 5;
const MAX_PROBES = 80;

/**
 * Where the chunk sits in the document, as a compact range, or null.
 *
 * Probes of ~5+ words are taken all along the chunk. Each hit votes for where the
 * chunk would START (hit position minus the probe's offset inside the chunk), and
 * the start with the most votes wins. Hits elsewhere in the file (a sentence the
 * abstract repeats, a running header) do not agree on a start and are outvoted.
 */
const locateChunk = (compact, chunkRaw) => {
  const words = searchWords(cleanChunk(chunkRaw));
  if (!words.length) return null;
  const full = compactOf(words.join(' '));
  if (full.length < 12) return null;

  const exact = findAll(compact, full, 0, compact.length, 2);
  if (exact.length === 1) return { start: exact[0], end: exact[0] + full.length, votes: 1, probes: 1 };

  // word index -> compact offset inside the chunk
  const offsets = [];
  let acc = 0;
  words.forEach((w) => { offsets.push(acc); acc += compactOf(w).length; });

  const step = Math.max(3, Math.ceil(words.length / MAX_PROBES));
  const probes = [];
  for (let w = 0; w < words.length; w += step) {
    let s = '';
    let e = w;
    while (e < words.length && (s.length < PROBE_MIN_CHARS || e - w < PROBE_MIN_WORDS)) {
      s += compactOf(words[e]);
      e++;
    }
    if (s.length < PROBE_MIN_CHARS && probes.length) break; // tail too short to be specific
    probes.push({ text: s, offset: offsets[w], id: probes.length });
    if (e >= words.length) break;
  }
  if (!probes.length) return null;

  const hits = [];
  probes.forEach((p) => {
    findAll(compact, p.text).forEach((at) => hits.push({ est: at - p.offset, at, len: p.text.length, id: p.id }));
  });
  if (!hits.length) return null;
  hits.sort((a, b) => a.est - b.est);

  // Text between the probes may be missing from or added to the file (summary lines,
  // superscripts, headers), so starts only need to agree loosely.
  const tolerance = Math.max(200, Math.round(full.length * 0.3));
  let best = null;
  let j = 0;
  for (let i = 0; i < hits.length; i++) {
    while (hits[i].est - hits[j].est > tolerance) j++;
    const ids = new Set();
    for (let k = j; k <= i; k++) ids.add(hits[k].id);
    if (!best || ids.size > best.votes) best = { from: j, to: i, votes: ids.size };
  }

  const needed = probes.length >= 4 ? 2 : 1;
  if (best.votes < needed) return null;

  const voters = hits.slice(best.from, best.to + 1);
  const start = Math.min(...voters.map((h) => h.at));
  const end = Math.max(...voters.map((h) => h.at + h.len));
  return { start, end: Math.min(end, start + Math.round(full.length * 1.5) + 200), votes: best.votes, probes: probes.length };
};

// =====================================================================
//  LOCATE THE PHRASE (inside a window)
// =====================================================================

const locatePhraseIn = (compact, phraseRaw, from, to) => {
  const words = searchWords(phraseRaw);
  const P = compactOf(words.join(' '));
  if (!P) return null;
  from = Math.max(0, from);
  to = Math.min(compact.length, to);

  const exact = findAll(compact, P, from, to, 1);
  if (exact.length) return { start: exact[0], end: exact[0] + P.length };

  // Long phrase with a small difference (a superscript, a footnote mark): accept it when
  // most of its 4-word pieces are found close together.
  if (words.length < 8) return null;
  const pieces = [];
  for (let w = 0; w + 4 <= words.length; w += 2) pieces.push(compactOf(words.slice(w, w + 4).join(' ')));
  const found = [];
  pieces.forEach((p) => {
    if (p.length < 12) return;
    const at = findAll(compact, p, from, to, 1)[0];
    if (at !== undefined) found.push({ at, len: p.length });
  });
  if (found.length < Math.ceil(pieces.length * 0.6)) return null;
  found.sort((a, b) => a.at - b.at);
  const start = found[0].at;
  const end = Math.max(...found.map((f) => f.at + f.len));
  if (end - start > P.length * 1.6 + 40) return null;
  return { start, end };
};

// =====================================================================
//  ENTRY POINT
// =====================================================================

/**
 * @param {string} concat  the document text, already folded with foldChar
 * @param {{chunk?: string, phrase?: string, caption?: string}} texts
 * @returns {{start: number, end: number, kind: 'phrase'|'chunk'|'caption'} | null}
 *          start/end are indexes into `concat` (end exclusive)
 */
export const locatePassage = (concat, { chunk = null, phrase = null, caption = null } = {}) => {
  if (!concat) return null;
  const { compact, pos } = buildCompactIndex(concat);
  if (!compact) return null;
  const toConcat = (r, kind) => ({ start: pos[r.start], end: pos[Math.max(r.start, r.end - 1)] + 1, kind });

  const region = chunk ? locateChunk(compact, chunk) : null;

  let captionAt = null;
  const C = caption ? compactOf(foldString(stripMarkdown(caption))) : '';
  if (C.length >= 8) {
    const lo = region ? Math.max(0, region.start - 3000) : 0;
    const hi = region ? region.end : compact.length;
    const hs = findAll(compact, C, lo, hi, 10);
    if (region && hs.length) {
      // the occurrence nearest to (at or before) the chunk is the table's own title
      const before = hs.filter((h) => h <= region.start + 50);
      captionAt = before.length ? before[before.length - 1] : hs[0];
    } else if (!region && hs.length === 1) {
      captionAt = hs[0];
    }
  }

  if (phrase) {
    const windows = [];
    if (region) {
      if (captionAt !== null && captionAt > region.start) windows.push([captionAt, region.end]);
      windows.push([region.start, region.end]);
      windows.push([region.start - 300, region.end + 1500]);
    } else if (captionAt !== null) {
      windows.push([captionAt, captionAt + 4000]);
    }
    for (const [from, to] of windows) {
      const hit = locatePhraseIn(compact, phrase, from, to);
      if (hit) return toConcat(hit, 'phrase');
    }
    if (!region && captionAt === null) {
      // No chunk to anchor on: only a long phrase that occurs exactly once is trusted.
      const P = compactOf(searchWords(phrase).join(' '));
      if (P.length >= 30) {
        const hs = findAll(compact, P, 0, compact.length, 2);
        if (hs.length === 1) return toConcat({ start: hs[0], end: hs[0] + P.length }, 'phrase');
      }
    }
  }

  if (region) return toConcat(region, 'chunk');
  if (captionAt !== null) return toConcat({ start: captionAt, end: captionAt + C.length }, 'caption');
  return null;
};

/** Text for the banner above a highlighted document. */
export const MATCH_MESSAGES = {
  phrase: 'Highlighted the cited text.',
  chunk: 'The exact quote was not found, so the source passage it came from is highlighted.',
  caption: 'Highlighted the table this value comes from.',
  notfound: 'Could not locate the cited passage in this file, so nothing is highlighted.',
};

// =====================================================================
//  PDF TEXT EXTRACTION
// =====================================================================

/**
 * The whole PDF as one folded string, with a map from every character back to the
 * text item and the character inside it. The map is what lets a highlight cover just
 * the matched words instead of every text item (often a whole line) they touch.
 *
 *   concat        folded text, items separated by one space
 *   charItem[i]   index into `items` for concat[i], or -1 for a separator
 *   charOff[i]    offset of concat[i] inside that item's str
 */
const extractPdfText = async (pdfDoc) => {
  const pages = [];
  const items = []; // { pageIdx, itemIdx }
  const chars = [];
  const charItem = [];
  const charOff = [];

  for (let i = 1; i <= pdfDoc.numPages; i++) {
    const page = await pdfDoc.getPage(i);
    const viewport = page.getViewport({ scale: 1 });
    const textContent = await page.getTextContent();

    const pageItems = textContent.items
      .filter((item) => item.str && item.str.trim().length > 0)
      .map((item, idx) => ({
        str: item.str,
        transform: item.transform,
        width: item.width || 0,
        height: item.height || 0,
        itemIndex: idx,
      }));

    pages.push({ pageIndex: i - 1, viewport, items: pageItems });

    pageItems.forEach((item, j) => {
      const globalIdx = items.length;
      items.push({ pageIdx: i - 1, itemIdx: j });
      if (chars.length && chars[chars.length - 1] !== ' ') {
        chars.push(' ');
        charItem.push(-1);
        charOff.push(0);
      }
      for (let k = 0; k < item.str.length; k++) {
        for (const c of foldChar(item.str[k])) {
          if (c === ' ' && chars.length && chars[chars.length - 1] === ' ') continue;
          chars.push(c);
          charItem.push(globalIdx);
          charOff.push(k);
        }
      }
    });
  }

  return { pages, items, concat: chars.join(''), charItem, charOff };
};

// =====================================================================
//  HIGHLIGHT RANGE
// =====================================================================

/**
 * texts = { caption, phrase, chunk }. Returns { segments, startPage, kind } or null.
 * segments: [{ pageIdx, itemIdx, from, to }]  (character range inside the item, inclusive)
 */
const findHighlightRange = (pdfTextData, texts) => {
  const { concat, charItem, charOff, items } = pdfTextData;
  const match = locatePassage(concat, texts);
  if (!match) {
    console.log('PdfHighlightViewer: no match found');
    return null;
  }

  const byItem = new Map();
  for (let i = match.start; i < match.end; i++) {
    const it = charItem[i];
    if (it < 0) continue;
    const cur = byItem.get(it);
    if (!cur) byItem.set(it, { from: charOff[i], to: charOff[i] });
    else { cur.from = Math.min(cur.from, charOff[i]); cur.to = Math.max(cur.to, charOff[i]); }
  }
  if (byItem.size === 0) return null;

  const segments = [...byItem.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([it, r]) => ({ ...items[it], ...r }));

  const uniquePages = [...new Set(segments.map((s) => s.pageIdx))];
  console.log(
    `PdfHighlightViewer: matched ${match.kind} → ${segments.length} items on page(s) [${uniquePages.join(', ')}]`
  );
  return { segments, startPage: segments[0].pageIdx, kind: match.kind };
};

// =====================================================================
//  HIGHLIGHT RECT COMPUTATION
// =====================================================================

const computeHighlightRects = (pdfTextData, segments) => {
  const rectsByPage = new Map();

  for (const { pageIdx, itemIdx, from, to } of segments) {
    const page = pdfTextData.pages[pageIdx];
    if (!page) continue;
    const item = page.items[itemIdx];
    if (!item) continue;

    const t = item.transform;
    const fontHeight = Math.max(Math.abs(t[0]), Math.abs(t[3])) || 12;
    const pageHeight = page.viewport.height;
    const len = item.str.length || 1;
    const fullWidth = item.width || fontHeight * 0.6 * len;

    // Only the matched characters of the item. Glyph widths are not known here, so the
    // share of the width is proportional to the share of characters.
    const left = t[4] + fullWidth * (from / len);
    const width = Math.max(fullWidth * ((to - from + 1) / len), 2);
    const top = pageHeight - t[5] - fontHeight * 0.85;
    const height = fontHeight * 1.15;

    if (!rectsByPage.has(pageIdx)) rectsByPage.set(pageIdx, []);
    rectsByPage.get(pageIdx).push({ left, top, width, height });
  }

  return rectsByPage;
};

// =====================================================================
//  PAGE CONTAINER DETECTION
// =====================================================================

const getPageContainer = (rootContainer, pageIdx) => {
  const byTestId = rootContainer.querySelector(
    `[data-testid="core__page-layer-${pageIdx}"]`
  );
  if (byTestId) return byTestId;

  const innerPage = rootContainer.querySelector(
    `[data-testid="core__inner-page-${pageIdx}"]`
  );
  if (innerPage) {
    return innerPage.querySelector('.rpv-core__page-layer') || innerPage;
  }

  const allLayers = rootContainer.querySelectorAll('.rpv-core__page-layer');
  return allLayers[pageIdx] || null;
};

// =====================================================================
//  DOM HIGHLIGHT INJECTION
// =====================================================================

const OVERLAY_CLASS = 'pdf-highlight-overlay';
const RECT_CLASS = 'pdf-highlight-rect';

const clearHighlightOverlays = (container) => {
  if (!container) return;
  container.querySelectorAll(`.${OVERLAY_CLASS}`).forEach((el) => el.remove());
};

const injectHighlightOverlays = (container, rectsByPage, pdfTextData) => {
  if (!container || !rectsByPage || rectsByPage.size === 0) return;

  rectsByPage.forEach((rects, pageIdx) => {
    const pageContainer = getPageContainer(container, pageIdx);
    if (!pageContainer) return; // not rendered yet; the observer injects it later

    const page = pdfTextData.pages[pageIdx];
    if (!page) return;

    const renderedWidth = pageContainer.clientWidth;
    const pdfWidth = page.viewport.width;
    if (!renderedWidth || !pdfWidth) return;
    const scale = renderedWidth / pdfWidth;

    if (getComputedStyle(pageContainer).position === 'static') {
      pageContainer.style.position = 'relative';
    }

    // Remove existing overlay on THIS page only
    pageContainer
      .querySelectorAll(`.${OVERLAY_CLASS}[data-highlight-page="${pageIdx}"]`)
      .forEach((el) => el.remove());

    const overlay = document.createElement('div');
    overlay.className = OVERLAY_CLASS;
    overlay.setAttribute('data-highlight-page', String(pageIdx));
    overlay.style.cssText =
      'position:absolute;top:0;left:0;width:100%;height:100%;pointer-events:none;z-index:1;';

    for (const rect of rects) {
      const div = document.createElement('div');
      div.className = RECT_CLASS;
      div.style.cssText =
        `position:absolute;` +
        `left:${rect.left * scale}px;` +
        `top:${rect.top * scale}px;` +
        `width:${rect.width * scale}px;` +
        `height:${rect.height * scale}px;` +
        `background:rgba(255,213,79,0.4);` +
        `border-radius:2px;` +
        `mix-blend-mode:multiply;`;
      overlay.appendChild(div);
    }

    pageContainer.appendChild(overlay);
  });
};

/**
 * scrollToHighlight — polls until the target page container exists in the
 * DOM, then scrolls it into view.  Returns a cancel function so the caller
 * can abort pending retries on unmount / re-render.
 *
 * When the page is far from the initial viewport and the Viewer hasn't
 * lazily rendered it yet, we first nudge the scroll container towards
 * the estimated position of that page — this triggers the Viewer to
 * mount the page layer, at which point the next poll picks it up.
 *
 * Once the page exists, it scrolls to the first highlight rect on it (not just
 * the top of the page), so a passage at the bottom of a page is visible.
 */
const scrollToHighlight = (container, pageIdx) => {
  if (!container) return () => {};

  const MAX_ATTEMPTS = 25;
  const POLL_INTERVAL = 200; // ms between retries
  let attempt = 0;
  let timerId = null;
  let cancelled = false;

  const getScrollParent = () => {
    const inner = container.querySelector('.rpv-core__inner-pages');
    return inner || container;
  };

  const estimatePageOffset = (scrollParent) => {
    const allPages = scrollParent.querySelectorAll('.rpv-core__page-layer');
    if (allPages.length === 0) return null;

    const samplePage = allPages[0];
    const pageHeight = samplePage.offsetHeight || samplePage.clientHeight;
    if (!pageHeight) return null;

    let gap = 0;
    if (allPages.length > 1) {
      const first = allPages[0].getBoundingClientRect();
      const second = allPages[1].getBoundingClientRect();
      gap = second.top - first.bottom;
      if (gap < 0) gap = 0;
    }

    return pageIdx * (pageHeight + gap);
  };

  const tryScroll = () => {
    if (cancelled) return;

    const target = getPageContainer(container, pageIdx);

    if (target) {
      const scrollParent = getScrollParent();
      const rect = target.querySelector(`.${RECT_CLASS}`);
      const parentRect = scrollParent.getBoundingClientRect();
      const targetRect = (rect || target).getBoundingClientRect();
      const lead = rect ? scrollParent.clientHeight * 0.3 : 0;
      const offset = targetRect.top - parentRect.top + scrollParent.scrollTop - lead;

      scrollParent.scrollTo({ top: Math.max(0, offset), behavior: 'smooth' });
      if (!rect && attempt < MAX_ATTEMPTS) {
        // the overlay lands a moment after the page; come back to centre on it
        attempt++;
        timerId = setTimeout(tryScroll, POLL_INTERVAL);
      }
      return;
    }

    attempt++;

    if (attempt < MAX_ATTEMPTS) {
      if (attempt % 3 === 1) {
        const scrollParent = getScrollParent();
        const estimated = estimatePageOffset(scrollParent);
        if (estimated !== null) {
          scrollParent.scrollTop = estimated;
        }
      }

      timerId = setTimeout(tryScroll, POLL_INTERVAL);
    }
  };

  timerId = setTimeout(tryScroll, 150);

  return () => {
    cancelled = true;
    clearTimeout(timerId);
  };
};

// =====================================================================
//  TOOLBAR STYLES
// =====================================================================

const toolbarStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '6px 10px',
  borderBottom: '1px solid #e0e4ea',
  background: '#f8f9fb',
  borderRadius: '4px 4px 0 0',
  flexShrink: 0,
};

const btnGroupStyle = { display: 'flex', alignItems: 'center', gap: '4px' };

const toolbarBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid #d0d5dd',
  background: '#fff',
  borderRadius: '4px',
  padding: '4px 8px',
  cursor: 'pointer',
  fontSize: '13px',
  color: '#344054',
  lineHeight: 1,
  minWidth: '28px',
  height: '28px',
};

const scaleLabelStyle = {
  fontSize: '12px',
  color: '#667085',
  minWidth: '42px',
  textAlign: 'center',
  userSelect: 'none',
  cursor: 'pointer',
};

const bannerBase = { padding: '5px 12px', fontSize: '12px', lineHeight: 1.4, borderBottom: '1px solid #e0e4ea', flexShrink: 0 };
const BANNER_COLORS = {
  phrase: { background: '#fff8e1', color: '#7a5b00' },
  caption: { background: '#fff8e1', color: '#7a5b00' },
  chunk: { background: '#eef4ff', color: '#3538cd' },
  notfound: { background: '#fef3f2', color: '#b42318' },
};

// =====================================================================
//  COMPONENT
// =====================================================================

const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_ZOOM_IDX = 2;

/**
 * PdfHighlightViewer
 *
 * Props:
 *   pdfUrl  — presigned S3 / direct URL to the PDF
 *   text1   — table caption (helps find the table when the chunk is not found)
 *   text2   — verbatim phrase (what gets highlighted, searched inside the chunk)
 *   text3   — chunk content (where in the file the citation came from)
 */
const PdfHighlightViewer = ({ pdfUrl, text1, text2, text3 }) => {
  const containerRef = useRef(null);
  const [highlightState, setHighlightState] = useState(null);
  const [matchKind, setMatchKind] = useState(null); // 'phrase' | 'chunk' | 'caption' | 'notfound' | null
  const [zoomIdx, setZoomIdx] = useState(DEFAULT_ZOOM_IDX);

  // Cache extracted text so zoom changes don't re-extract. Keyed by URL so a
  // different document never reuses another one's text.
  const pdfTextCacheRef = useRef(null);

  const currentScale = ZOOM_STEPS[zoomIdx];

  const handleDocumentLoad = useCallback(
    async (e) => {
      const pdfDoc = e.doc;
      try {
        if (!pdfTextCacheRef.current || pdfTextCacheRef.current.url !== pdfUrl) {
          pdfTextCacheRef.current = { url: pdfUrl, data: await extractPdfText(pdfDoc) };
        }
        const pdfTextData = pdfTextCacheRef.current.data;

        if (!text1 && !text2 && !text3) return;

        const range = findHighlightRange(pdfTextData, { caption: text1, phrase: text2, chunk: text3 });
        if (!range) {
          setHighlightState(null);
          setMatchKind('notfound');
          return;
        }

        const rectsByPage = computeHighlightRects(pdfTextData, range.segments);
        setHighlightState({ rectsByPage, startPage: range.startPage, pdfTextData });
        setMatchKind(range.kind);
      } catch (err) {
        console.error('PdfHighlightViewer: error during highlight pipeline', err);
      }
    },
    [pdfUrl, text1, text2, text3],
  );

  // Inject overlays after pages render
  useEffect(() => {
    if (!highlightState || !containerRef.current) return;

    const container = containerRef.current;
    const { rectsByPage, startPage, pdfTextData } = highlightState;

    const doInject = () => {
      clearHighlightOverlays(container);
      injectHighlightOverlays(container, rectsByPage, pdfTextData);
    };

    doInject();
    const cancelScroll = scrollToHighlight(container, startPage);

    // MutationObserver for re-renders (zoom, lazy page load)
    let isInjecting = false;
    let debounceTimer = null;

    const observer = new MutationObserver((mutations) => {
      if (isInjecting) return;

      const isOwnMutation = mutations.every((m) => {
        const nodes = [...Array.from(m.addedNodes || []), ...Array.from(m.removedNodes || [])];
        return nodes.length > 0 && nodes.every(
          (n) => n.nodeType === 1 && n.classList && n.classList.contains(OVERLAY_CLASS),
        );
      });
      if (isOwnMutation) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        isInjecting = true;
        doInject();
        requestAnimationFrame(() => { isInjecting = false; });
      }, 250);
    });

    observer.observe(container, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style'],
    });

    return () => {
      cancelScroll();
      observer.disconnect();
      clearTimeout(debounceTimer);
      clearHighlightOverlays(container);
    };
  }, [highlightState, zoomIdx]);

  // Toolbar handlers
  const handleZoomIn = () => setZoomIdx((p) => Math.min(p + 1, ZOOM_STEPS.length - 1));
  const handleZoomOut = () => setZoomIdx((p) => Math.max(p - 1, 0));
  const handleZoomReset = () => setZoomIdx(DEFAULT_ZOOM_IDX);
  const handleDownload = () => pdfUrl && window.open(pdfUrl, '_blank', 'noopener,noreferrer');

  return (
    <Worker workerUrl={PDF_WORKER_URL}>
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {/* Toolbar */}
        <div style={toolbarStyle}>
          <div style={btnGroupStyle}>
            <button
              style={{ ...toolbarBtnStyle, opacity: zoomIdx <= 0 ? 0.4 : 1 }}
              onClick={handleZoomOut}
              disabled={zoomIdx <= 0}
              title="Zoom Out"
            >
              <i className="bi bi-dash"></i>
            </button>
            <span style={scaleLabelStyle} onClick={handleZoomReset} title="Reset Zoom">
              {Math.round(currentScale * 100)}%
            </span>
            <button
              style={{ ...toolbarBtnStyle, opacity: zoomIdx >= ZOOM_STEPS.length - 1 ? 0.4 : 1 }}
              onClick={handleZoomIn}
              disabled={zoomIdx >= ZOOM_STEPS.length - 1}
              title="Zoom In"
            >
              <i className="bi bi-plus"></i>
            </button>
          </div>
          <div style={btnGroupStyle}>
            <button style={toolbarBtnStyle} onClick={handleDownload} title="Open in New Tab">
              <i className="bi bi-download"></i>
            </button>
          </div>
        </div>

        {matchKind && (
          <div style={{ ...bannerBase, ...BANNER_COLORS[matchKind] }}>{MATCH_MESSAGES[matchKind]}</div>
        )}

        {/* PDF Viewer — key forces re-mount on zoom so defaultScale applies */}
        <div ref={containerRef} style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Viewer
            key={`pdf-zoom-${currentScale}`}
            fileUrl={pdfUrl}
            defaultScale={currentScale}
            onDocumentLoad={handleDocumentLoad}
          />
        </div>
      </div>
    </Worker>
  );
};

export default PdfHighlightViewer;